<template>
  <b-card>
    <div class="legacy-customer-hint legacy-customer-hint--info mb-2">
      <div class="legacy-customer-hint__icon">
        <feather-icon
          icon="HelpCircleIcon"
          size="22"
        />
      </div>
      <div class="legacy-customer-hint__body">
        <div class="legacy-customer-hint__title">
          Wann nutze ich die Bestands-Erfassung?
        </div>
        <div class="legacy-customer-hint__detail">
          Diese Maske ist für Mietverträge gedacht, die <strong>vor Einführung dieser Plattform abgeschlossen</strong> wurden und für die ein <strong>unterzeichneter Original-Vertrag</strong> vorliegt (typischerweise als gescanntes PDF). Der Auftrag wird stumm angelegt — der Kunde erhält weder eine Bestätigungs-Mail noch eine Zoho-Signatur-Anfrage, da der Vertrag bereits unterschrieben ist.
        </div>
        <div class="legacy-customer-hint__meta">
          Für <strong>neue Vermietungen</strong>, die erst jetzt zustande kommen, bitte
          <router-link to="/booking">
            Direktbuchung
          </router-link>
          oder
          <router-link to="/create-offer">
            Direktangebot
          </router-link>
          verwenden — dort läuft der reguläre Anfragen-/Angebots-/Signatur-Prozess.
        </div>
      </div>
    </div>
    <validation-observer ref="legacyForm">
      <form-wizard
        color="#7367F0"
        :title="null"
        :subtitle="null"
        layout="horizontal"
        finish-button-text="Bestands-Auftrag anlegen"
        back-button-text="Zurück"
        next-button-text="Weiter"
        class="wizard-vertical mb-3"
        @on-complete="submit"
      >
        <tab-content
          title="Kunde"
          icon="feather icon-user"
          :before-change="() => validateStep('customerStep')"
        >
          <validation-observer ref="customerStep">
            <div
              v-if="existingCustomerMatch && !useExistingCustomer"
              class="legacy-customer-hint legacy-customer-hint--info mb-2"
            >
              <div class="legacy-customer-hint__icon">
                <feather-icon
                  icon="UserCheckIcon"
                  size="22"
                />
              </div>
              <div class="legacy-customer-hint__body">
                <div class="legacy-customer-hint__title">
                  Bestehender Kunde gefunden
                </div>
                <div class="legacy-customer-hint__detail">
                  <strong>{{ existingCustomerMatch.firstName }} {{ existingCustomerMatch.lastName }}</strong>
                  <span v-if="existingCustomerAddress">— {{ existingCustomerAddress }}</span>
                </div>
                <div class="legacy-customer-hint__meta">
                  <span>Kundennr. #{{ existingCustomerMatch.id }}</span>
                  <span v-if="existingCustomerMatch.created_at">
                    · angelegt am {{ formatDate(existingCustomerMatch.created_at) }}
                  </span>
                </div>
              </div>
              <div class="legacy-customer-hint__actions">
                <b-button
                  variant="primary"
                  size="sm"
                  @click="adoptExistingCustomer"
                >
                  <feather-icon
                    icon="CheckIcon"
                    size="14"
                    class="mr-25"
                  />
                  Bestehenden verwenden
                </b-button>
                <b-button
                  variant="flat-secondary"
                  size="sm"
                  @click="forceNewCustomer"
                >
                  Neuen anlegen
                </b-button>
              </div>
            </div>
            <div
              v-if="useExistingCustomer && existingCustomerId"
              class="legacy-customer-hint legacy-customer-hint--success mb-2"
            >
              <div class="legacy-customer-hint__icon">
                <feather-icon
                  icon="LinkIcon"
                  size="22"
                />
              </div>
              <div class="legacy-customer-hint__body">
                <div class="legacy-customer-hint__title">
                  Bestehender Kunde verknüpft (#{{ existingCustomerId }})
                </div>
                <div class="legacy-customer-hint__meta">
                  Stammdaten bleiben unverändert. Die neue Buchung wird diesem Kunden zugeordnet.
                </div>
              </div>
              <div class="legacy-customer-hint__actions">
                <b-button
                  variant="flat-secondary"
                  size="sm"
                  @click="resetCustomerSelection"
                >
                  <feather-icon
                    icon="XIcon"
                    size="14"
                    class="mr-25"
                  />
                  Verknüpfung lösen
                </b-button>
              </div>
            </div>
            <div
              v-if="forcedNewWithDuplicate"
              class="legacy-customer-hint legacy-customer-hint--warning mb-2"
            >
              <div class="legacy-customer-hint__icon">
                <feather-icon
                  icon="AlertTriangleIcon"
                  size="22"
                />
              </div>
              <div class="legacy-customer-hint__body">
                <div class="legacy-customer-hint__title">
                  Duplikat wird angelegt
                </div>
                <div class="legacy-customer-hint__meta">
                  Sie legen einen <strong>zweiten Kunden</strong> mit derselben E-Mail an. Falls das nicht gewollt ist, bestehenden Kunden verwenden.
                </div>
              </div>
              <div class="legacy-customer-hint__actions">
                <b-button
                  v-if="existingCustomerMatch"
                  variant="flat-warning"
                  size="sm"
                  @click="adoptExistingCustomer"
                >
                  Doch verknüpfen
                </b-button>
              </div>
            </div>
            <fieldset :disabled="customerLocked">
              <b-row>
                <b-col md="4">
                  <b-form-group label="Anrede">
                    <v-select
                      v-model="customer.salutation"
                      :options="salutations"
                      :clearable="true"
                      :disabled="customerLocked"
                      placeholder="Optional"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <validation-provider
                    v-slot="{ errors }"
                    name="Vorname"
                    rules="required"
                  >
                    <b-form-group label="Vorname *">
                      <b-form-input
                        v-model="customer.firstName"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col md="4">
                  <validation-provider
                    v-slot="{ errors }"
                    name="Nachname"
                    rules="required"
                  >
                    <b-form-group label="Nachname *">
                      <b-form-input
                        v-model="customer.lastName"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>

                <b-col md="6">
                  <validation-provider
                    v-slot="{ errors }"
                    name="E-Mail"
                    rules="required|email"
                  >
                    <b-form-group label="E-Mail *">
                      <b-form-input
                        v-model="customer.email"
                        type="email"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col md="6">
                  <validation-provider
                    v-slot="{ errors }"
                    name="Telefonnummer"
                    rules="required"
                  >
                    <b-form-group label="Telefonnummer *">
                      <b-form-input
                        v-model="customer.phoneNumber"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>

                <b-col md="6">
                  <b-form-group label="Mobilnummer">
                    <b-form-input v-model="customer.mobileNumber" />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Firmenname (optional)">
                    <b-form-input v-model="customer.organizationName" />
                  </b-form-group>
                </b-col>

                <b-col md="8">
                  <validation-provider
                    v-slot="{ errors }"
                    name="Straße"
                    rules="required"
                  >
                    <b-form-group label="Straße *">
                      <b-form-input
                        v-model="customer.street"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col md="4">
                  <validation-provider
                    v-slot="{ errors }"
                    name="Hausnummer"
                    rules="required"
                  >
                    <b-form-group label="Hausnummer *">
                      <b-form-input
                        v-model="customer.streetNumber"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>

                <b-col md="4">
                  <validation-provider
                    v-slot="{ errors }"
                    name="PLZ"
                    rules="required"
                  >
                    <b-form-group label="PLZ *">
                      <b-form-input
                        v-model="customer.zipCode"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col md="8">
                  <validation-provider
                    v-slot="{ errors }"
                    name="Ort"
                    rules="required"
                  >
                    <b-form-group label="Ort *">
                      <b-form-input
                        v-model="customer.city"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>
              </b-row>
            </fieldset>
          </validation-observer>
        </tab-content>

        <tab-content
          title="Container"
          icon="feather icon-archive"
          :before-change="() => validateStep('storageStep')"
        >
          <validation-observer ref="storageStep">
            <b-row>
              <b-col md="12">
                <b-form-group
                  label="Standort"
                  description="Filtert die Container-Liste auf einen Standort."
                >
                  <v-select
                    v-model="selectedLocation"
                    :options="locationOptions"
                    :get-option-label="locationOptionLabel"
                    :reduce="opt => opt.id"
                    :clearable="false"
                  />
                </b-form-group>
              </b-col>
              <b-col md="12">
                <validation-provider
                  v-slot="{ errors }"
                  name="Container"
                  rules="required"
                >
                  <b-form-group
                    label="Container auswählen *"
                    description="Freie Container zuerst. Aktiv vermietete (laufende Verträge) sind grau und unten."
                  >
                    <v-select
                      v-model="selectedStorage"
                      :options="storageOptions"
                      :get-option-label="storageLabel"
                      :reduce="opt => opt"
                      placeholder="Container suchen (ID oder Name)..."
                      :class="errors.length > 0 ? 'is-invalid' : ''"
                    >
                      <template #option="opt">
                        <span :class="opt.isActivelyRented ? 'text-muted' : ''">
                          {{ storageLabel(opt) }}
                          <small v-if="opt.isActivelyRented">— aktiv vermietet</small>
                        </span>
                      </template>
                      <template #no-options>
                        Keine Container an diesem Standort gefunden.
                      </template>
                    </v-select>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col
                v-if="selectedStorage && selectedStorage.isActivelyRented"
                md="12"
              >
                <b-alert
                  show
                  variant="warning"
                >
                  Achtung: Dieser Container hat einen laufenden Vertrag (OrderItem ohne <code>moveOutDate</code>). Bei Bestands-Importen meist nicht gewollt — bitte vorher prüfen.
                </b-alert>
              </b-col>
            </b-row>
          </validation-observer>
        </tab-content>

        <tab-content
          title="Anhänge"
          icon="feather icon-paperclip"
        >
          <p class="text-muted">
            Optional. Gescannte Original-Dokumente (Vertrag, SEPA, Ausweis ...) werden nach dem Speichern am Kunden hinterlegt. Erlaubt: PDF, JPG, PNG.
          </p>
          <b-row v-if="visibleAttachmentTypes.length === 0">
            <b-col md="12">
              <b-alert
                show
                variant="info"
              >
                Keine Dokumenttypen verfügbar.
              </b-alert>
            </b-col>
          </b-row>
          <b-row
            v-for="type in visibleAttachmentTypes"
            :key="type.id"
            class="mb-3"
          >
            <b-col md="12">
              <h5 class="mb-0">
                {{ type.attachmentType }}
              </h5>
              <small
                v-if="type.description"
                class="text-muted d-block mb-1"
              >
                {{ type.description }}
              </small>
              <b-form-file
                v-model="attachments[type.id].files"
                multiple
                accept="application/pdf,image/jpeg,image/png"
                placeholder="Datei(en) auswählen oder hineinziehen..."
                drop-placeholder="Hier ablegen..."
                browse-text="Durchsuchen"
              />
              <b-form-input
                v-model="attachments[type.id].note"
                placeholder="Notiz (optional)"
                class="mt-1"
              />
            </b-col>
          </b-row>
        </tab-content>

        <tab-content
          title="Mietdaten"
          icon="feather icon-calendar"
          :before-change="() => validateStep('rentStep')"
        >
          <validation-observer ref="rentStep">
            <b-row>
              <b-col md="6">
                <validation-provider
                  v-slot="{ errors }"
                  name="Mietbeginn"
                  rules="required"
                >
                  <b-form-group label="Mietbeginn *">
                    <flatpickr
                      v-model="rent.rentStart"
                      class="form-control"
                      :config="dateConfig"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col md="6">
                <b-form-group
                  label="Mietende (optional)"
                  description="Leer lassen für unbestimmte Laufzeit."
                >
                  <flatpickr
                    v-model="rent.rentEnd"
                    class="form-control"
                    :config="dateConfig"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <validation-provider
                  v-slot="{ errors }"
                  name="Miete"
                  rules="required"
                >
                  <b-form-group label="Monatliche Miete (€) *">
                    <b-form-input
                      v-model.number="rent.price"
                      type="number"
                      min="0"
                      step="0.01"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small
                      v-if="rent.price > 0"
                      class="text-muted"
                    >
                      Anzeige: {{ formattedPrice }}
                    </small>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>
            <b-row class="mt-2">
              <b-col md="12">
                <b-card
                  bg-variant="light"
                  class="mb-0"
                >
                  <h5 class="mb-1">
                    Zusammenfassung
                  </h5>
                  <div><strong>Kunde:</strong> {{ customer.firstName }} {{ customer.lastName }} ({{ customer.email }})</div>
                  <div><strong>Container:</strong> {{ selectedStorage ? storageLabel(selectedStorage) : '—' }}</div>
                  <div><strong>Mietbeginn:</strong> {{ rent.rentStart || '—' }}</div>
                  <div><strong>Mietende:</strong> {{ rent.rentEnd || 'unbestimmt' }}</div>
                  <div><strong>Monatliche Miete:</strong> {{ formattedPrice }}</div>
                  <div><strong>Anhänge:</strong> {{ attachmentSummaryText }}</div>
                  <hr>
                  <small class="text-muted">
                    Es werden keine E-Mails und keine Zoho-Signatur-Anfragen versendet. Der Auftrag wird intern als Bestands-Import markiert.
                  </small>
                </b-card>
              </b-col>
            </b-row>
          </validation-observer>
        </tab-content>
      </form-wizard>
    </validation-observer>
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormFile,
  BAlert,
  BButton,
} from 'bootstrap-vue'
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import Vueflatpickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.min.css'
import { required, email } from '@validations'
import helper from '@helper/helper'
import Vue from 'vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { createLegacyOrder } from '@/service/legacyOrderService'

export default {
  name: 'LegacyOrderWizard',
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormFile,
    BAlert,
    BButton,
    FormWizard,
    TabContent,
    ValidationObserver,
    ValidationProvider,
    vSelect,
    flatpickr: Vueflatpickr,
  },
  data() {
    return {
      required,
      email,
      salutations: ['Herr', 'Frau', 'Divers'],
      customer: {
        salutation: null,
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        mobileNumber: '',
        organizationName: '',
        street: '',
        streetNumber: '',
        zipCode: '',
        city: '',
        country: 'Deutschland',
      },
      selectedLocation: null,
      selectedStorage: null,
      rent: {
        rentStart: '',
        rentEnd: '',
        price: null,
      },
      dateConfig: {
        altInput: true,
        altFormat: 'd.m.Y',
        dateFormat: 'Z',
        locale: { firstDayOfWeek: 1 },
      },
      attachments: {},
      existingCustomerId: null,
      useExistingCustomer: false,
      forcedNewWithDuplicate: false,
      submitting: false,
    }
  },
  computed: {
    activeStorageIds() {
      const items = this.$store.getters.orderItemsEntries || []
      const ids = new Set()
      items.forEach(it => {
        if (it.moveOutDate === null) {
          const sid = it.storage_id || (it.storage && it.storage.id)
          if (sid) ids.add(sid)
        }
      })
      return ids
    },
    enrichedStorages() {
      const storages = this.$store.getters.storageEntries || []
      const active = this.activeStorageIds
      return storages.map(s => ({ ...s, isActivelyRented: active.has(s.id) }))
    },
    locationOptions() {
      const all = this.enrichedStorages
      const groups = new Map()
      all.forEach(s => {
        if (!s.location) return
        const { id } = s.location
        if (!groups.has(id)) {
          groups.set(id, {
            id, name: s.location.name, total: 0, free: 0,
          })
        }
        const g = groups.get(id)
        g.total += 1
        if (!s.isActivelyRented) g.free += 1
      })
      const grandTotal = all.length
      const grandFree = all.filter(s => !s.isActivelyRented).length
      const list = [{
        id: null, name: 'Alle Standorte', total: grandTotal, free: grandFree,
      }]
      Array.from(groups.values())
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach(g => list.push(g))
      return list
    },
    storageOptions() {
      const filtered = this.selectedLocation
        ? this.enrichedStorages.filter(s => s.location && s.location.id === this.selectedLocation)
        : this.enrichedStorages
      return [...filtered].sort((a, b) => {
        if (a.isActivelyRented !== b.isActivelyRented) {
          return a.isActivelyRented ? 1 : -1
        }
        return (a.storageIdent || '').localeCompare(b.storageIdent || '')
      })
    },
    formattedPrice() {
      const value = Number(this.rent.price)
      if (!Number.isFinite(value) || value <= 0) return '—'
      const { formatGermanCurrency } = helper()
      return formatGermanCurrency(value)
    },
    visibleAttachmentTypes() {
      const types = this.$store.getters.getAttachmentsTypes || []
      return types.filter(t => t && t.attachmentType !== 'ShopUploads')
    },
    attachmentSections() {
      return this.visibleAttachmentTypes
        .map(t => {
          const slot = this.attachments[t.id]
          return {
            typeId: t.id,
            label: t.attachmentType,
            files: slot ? slot.files || [] : [],
            note: slot ? slot.note || '' : '',
          }
        })
        .filter(s => s.files.length > 0)
    },
    totalAttachmentCount() {
      return this.attachmentSections.reduce((sum, s) => sum + s.files.length, 0)
    },
    attachmentSummaryText() {
      if (this.totalAttachmentCount === 0) return 'keine'
      const parts = this.attachmentSections.map(s => `${s.files.length}× ${s.label}`)
      return `${this.totalAttachmentCount} Dokument${this.totalAttachmentCount === 1 ? '' : 'e'} (${parts.join(', ')})`
    },
    existingCustomerMatch() {
      const value = this.customer.email
      if (!value) return null
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return null
      const finder = this.$store.getters.customerExistByEmail
      const match = typeof finder === 'function' ? finder(value) : null
      if (!match || !match.id) return null
      if (this.existingCustomerId && match.id === this.existingCustomerId) return match
      return match
    },
    customerLocked() {
      return this.useExistingCustomer && !!this.existingCustomerId
    },
    existingCustomerAddress() {
      const m = this.existingCustomerMatch
      if (!m) return ''
      const street = [m.street, m.streetNumber].filter(Boolean).join(' ')
      const city = [m.zipCode, m.city].filter(Boolean).join(' ')
      return [street, city].filter(Boolean).join(', ')
    },
  },
  watch: {
    selectedLocation() {
      if (this.selectedStorage
        && this.selectedLocation
        && this.selectedStorage.location
        && this.selectedStorage.location.id !== this.selectedLocation) {
        this.selectedStorage = null
      }
    },
    visibleAttachmentTypes: {
      immediate: true,
      handler(types) {
        types.forEach(t => {
          if (!this.attachments[t.id]) {
            Vue.set(this.attachments, t.id, { files: [], note: '' })
          }
        })
      },
    },
    'customer.email': function onEmailChange(newVal, oldVal) {
      if (newVal === oldVal) return
      if (this.useExistingCustomer) {
        this.existingCustomerId = null
        this.useExistingCustomer = false
      }
      this.forcedNewWithDuplicate = false
    },
  },
  created() {
    if (!this.$store.getters.storageEntries || this.$store.getters.storageEntries.length === 0) {
      this.$store.dispatch('getStorageEntries')
    }
    if (!this.$store.getters.orderItemsEntries || this.$store.getters.orderItemsEntries.length === 0) {
      this.$store.dispatch('getOrderItemsEntries')
    }
    const types = this.$store.getters.getAttachmentsTypes
    if (!types || types.length === 0) {
      this.$store.dispatch('getAttachmentsTypesEntries')
    }
    const customers = this.$store.getters.customerEntries
    if (!customers || customers.length === 0) {
      this.$store.dispatch('getCustomerEntries')
    }
  },
  methods: {
    storageLabel(storage) {
      if (!storage) return ''
      const ident = storage.storageIdent || `#${storage.id}`
      const name = storage.name && storage.name !== ident ? ` — ${storage.name}` : ''
      const size = storage.storageSpace ? ` (${storage.storageSpace} m²)` : ''
      const loc = !this.selectedLocation && storage.location && storage.location.name
        ? ` @ ${storage.location.name}`
        : ''
      return `${ident}${name}${size}${loc}`
    },
    locationOptionLabel(opt) {
      if (!opt) return ''
      return `${opt.name} — ${opt.free} frei von ${opt.total}`
    },
    async validateStep(refName) {
      const ref = this.$refs[refName]
      if (!ref) return true
      const isValid = await ref.validate()
      return isValid
    },
    showToast(title, variant, text = '') {
      Vue.$toast({
        component: ToastificationContent,
        props: {
          title, variant, text, icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
        },
      })
    },
    formatDate(value) {
      if (!value) return ''
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleDateString('de-DE')
    },
    adoptExistingCustomer() {
      const match = this.existingCustomerMatch
      if (!match) return
      this.existingCustomerId = match.id
      this.useExistingCustomer = true
      this.forcedNewWithDuplicate = false
      const keep = match
      this.customer = {
        salutation: keep.salutation || null,
        firstName: keep.firstName || '',
        lastName: keep.lastName || '',
        email: keep.email || this.customer.email,
        phoneNumber: keep.phoneNumber || '',
        mobileNumber: keep.mobileNumber || '',
        organizationName: keep.organizationName || '',
        street: keep.street || '',
        streetNumber: keep.streetNumber || '',
        zipCode: keep.zipCode || '',
        city: keep.city || '',
        country: keep.country || 'Deutschland',
      }
    },
    forceNewCustomer() {
      this.existingCustomerId = null
      this.useExistingCustomer = false
      this.forcedNewWithDuplicate = true
    },
    resetCustomerSelection() {
      this.existingCustomerId = null
      this.useExistingCustomer = false
      this.forcedNewWithDuplicate = false
    },
    async submit() {
      if (this.submitting) return
      this.submitting = true
      try {
        const customerPayload = this.existingCustomerId
          ? { id: this.existingCustomerId }
          : { ...this.customer }
        const result = await createLegacyOrder(this.$store, {
          customer: customerPayload,
          storage: { id: this.selectedStorage.id },
          rent: {
            rentStart: this.rent.rentStart,
            rentEnd: this.rent.rentEnd || null,
            price: Number(this.rent.price),
          },
          attachments: this.attachmentSections,
        })
        const errs = result.attachmentErrors || []
        if (errs.length === 0) {
          const docHint = this.totalAttachmentCount > 0
            ? ` (${this.totalAttachmentCount} Dokumente am Kunden hinterlegt)`
            : ''
          this.showToast(
            'Bestands-Auftrag angelegt',
            'success',
            `Auftrag #${result.order.id} wurde gespeichert${docHint}.`,
          )
        } else {
          this.showToast(
            'Auftrag angelegt — Anhänge teilweise fehlgeschlagen',
            'warning',
            `${errs.length} Dokumenten-Gruppe(n) konnte(n) nicht hochgeladen werden. Bitte später im Kunden-Profil nachreichen.`,
          )
        }
        this.$router.push({ name: 'orders-view', params: { id: result.order.id } })
      } catch (err) {
        this.showToast('Fehler beim Speichern', 'danger', err.message || 'Unbekannter Fehler')
        this.submitting = false
      }
    },
  },
}
</script>

<style lang="scss">
@import '~vue-select/dist/vue-select.css';

.legacy-customer-hint {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 0.5rem;
  border: 1px solid transparent;
  background-color: #f8f9fa;

  &__icon {
    flex-shrink: 0;
    width: 2.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    background-color: rgba(0, 0, 0, 0.06);
  }

  &__body {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__title {
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.3;
    color: inherit;
  }

  &__detail {
    margin-top: 0.125rem;
    font-size: 0.9375rem;
    line-height: 1.4;
    color: #5e5873;
  }

  &__meta {
    margin-top: 0.25rem;
    font-size: 0.8125rem;
    line-height: 1.4;
    color: #6e6b7b;
  }

  &__actions {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  @media (max-width: 768px) {
    flex-wrap: wrap;

    &__actions {
      width: 100%;
      margin-top: 0.5rem;
      justify-content: flex-end;
    }
  }

  &--info {
    border-color: rgba(0, 207, 232, 0.4);
    background-color: rgba(0, 207, 232, 0.08);

    .legacy-customer-hint__icon {
      background-color: rgba(0, 207, 232, 0.18);
      color: #00b4cc;
    }

    .legacy-customer-hint__title { color: #00859a; }
  }

  &--success {
    border-color: rgba(40, 199, 111, 0.4);
    background-color: rgba(40, 199, 111, 0.08);

    .legacy-customer-hint__icon {
      background-color: rgba(40, 199, 111, 0.18);
      color: #28c76f;
    }

    .legacy-customer-hint__title { color: #1f8f4f; }
  }

  &--warning {
    border-color: rgba(255, 159, 67, 0.4);
    background-color: rgba(255, 159, 67, 0.08);

    .legacy-customer-hint__icon {
      background-color: rgba(255, 159, 67, 0.2);
      color: #ff9f43;
    }

    .legacy-customer-hint__title { color: #b56918; }
  }
}
</style>
